


import { Component, Prop, Vue, Watch } from "vue-property-decorator";

import { AsYouType, getExampleNumber, parsePhoneNumberFromString } from "libphonenumber-js";
/* eslint-disable-next-line */
// @ts-ignore — JSON resource shipped by libphonenumber-js, no type declaration.
import examples from "libphonenumber-js/examples.mobile.json";

import { CustomError } from "@Internal/Models";

import { PhoneCodeCountries } from "App/IOC";
import { Country } from "App/Models";

import SimpleSelect from "Web/Components/V2/Select/Simple.vue";

/* Phone field with a searchable country flag + a national-format number input, mirroring the
   desktop Prospeneo contact phone widget. Value in/out is E.164 ("+33608897922"): parsed on load
   to pick the flag + show the national format, re-emitted as E.164 once the number is valid. */
@Component( {
    components: {
        SimpleSelect
    }
})
export default class PhoneNumberField extends Vue {
    @Prop( { default: "" } )
    readonly Id!: string;

    @Prop()
    readonly Value?: string;

    private countries: Country[] = PhoneCodeCountries;
    /* Literal default — no `this` call / array lookup in the initializer (either leaves `country`
       undefined at first render in vue-class-component). Refined from the list in created(). */
    private country: Country = { Id: "fr", ShortName: "FR", Name: "France", DialCode: "33" };
    private display: string = "";
    private invalid: boolean = false;
    /* True while the field has focus. The parent round-trips our emitted value back through the
       Value prop; without this guard, an incomplete (invalid) number mid-typing emits `undefined`,
       the parent clears Phone, and the Value watcher would hydrate "" — wiping what's being typed.
       We skip the watcher while editing and let AsYouType drive the display instead. */
    private editing: boolean = false;

    private created(): void {
        this.country = this.resolveCountry( "FR" );

        if( this.Value )
            this.hydrate( this.Value );
    }

    /* Reformat when the parent pushes a fresh value (e.g. after save + reload). */
    @Watch( "Value" )
    private onValueChange( value?: string ): void {
        /* Ignore the parent echoing our own value back while the user is typing — only react to
           genuine external changes (e.g. save + reload). */
        if( this.editing )
            return;

        this.hydrate( value !== undefined ? value : "" );
    }

    private get hint(): string {
        const example = getExampleNumber( this.country.ShortName, examples );

        if( example === undefined )
            return "";

        /* FR example numbers come out as 06 12 34 56 78; force a neutral 0-filled mask instead. */
        return this.country.ShortName === "FR" ? "06 00 00 00 00" : example.formatNational();
    }

    private resolveCountry( shortName: string ): Country {
        const match = this.countries.find( country => country.ShortName === shortName );

        return match !== undefined ? match : this.countries[ 0 ];
    }

    private hydrate( value: string ): void {
        if( value === "" ) {
            this.display = "";
            this.invalid = false;
            return;
        }

        const parsed = parsePhoneNumberFromString( value );

        if( parsed === undefined ) {
            this.display = value;
            this.invalid = true;
            return;
        }

        if( parsed.country !== undefined )
            this.country = this.resolveCountry( parsed.country );

        this.display = parsed.formatNational();
        this.invalid = !parsed.isValid();
    }

    private getCountries( keyword: string = "" ): Promise<[ Country[], CustomError | undefined ]> {
        const needle = keyword.trim().toLowerCase();

        const list = needle === ""
            ? this.countries
            : this.countries.filter( country =>
                country.Name.toLowerCase().includes( needle ) ||
                country.DialCode.includes( needle ) );

        return Promise.resolve( [ this.franceFirst( list ), undefined ] );
    }

    /* Pin France to the top of the dropdown (the dominant locale) while keeping the rest in their
       original alphabetical order. */
    private franceFirst( list: Country[] ): Country[] {
        const france = list.find( country => country.ShortName === "FR" );

        if( france === undefined )
            return list;

        return [ france, ...list.filter( country => country.ShortName !== "FR" ) ];
    }

    private selectCountry( country: Country ): void {
        this.country = country;
        this.format( this.display );
    }

    private onInput( event: Event ): void {
        this.format( ( event.target as HTMLInputElement ).value );
    }

    /* Format as the user types (national format of the selected country), detect the country from a
       pasted international number, and emit E.164 only when the number is valid. */
    private format( raw: string ): void {
        if( raw.trim() === "" ) {
            this.display = "";
            this.invalid = false;
            this.$emit( "Value", undefined );
            return;
        }

        this.display = new AsYouType( this.country.ShortName ).input( raw );

        const parsed = parsePhoneNumberFromString( raw, this.country.ShortName );

        if( parsed !== undefined && parsed.country !== undefined )
            this.country = this.resolveCountry( parsed.country );

        if( parsed !== undefined && parsed.isValid() ) {
            this.invalid = false;
            this.$emit( "Value", parsed.number );
            return;
        }

        this.invalid = true;
        this.$emit( "Value", undefined );
    }
}
